import { Address } from 'types/form';
import big from 'big.js';
import moment from 'moment';
import { realEstateStrategyColors } from '@src/constants/real-estate-strategies';
import ReitElement from '@classes/ReitElement';

export function reitNamesFromElements(reitElement: ReitElement): string {
	let formattedNames = '';

	if (reitElement.reits) {
		if (reitElement.reits.length > 3) {
			formattedNames = '&bull; Multiple eREITs';
		} else {
			const reitNames = reitElement.reits.map((reit: { name: string }) => reit.name);
			formattedNames = '&bull; ' + reitNames.slice(0, 3).join(', ');
		}
	}

	return formattedNames;
}

export function number(input: string | number | undefined | null, decimals = 0, addCommas = true): string {
	if (input !== null && input !== undefined && input !== '' && !isNaN(Number(input))) {
		const arr = big(input).toFixed(decimals).split('.');
		if (addCommas) {
			arr[0] = arr[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
		}
		return arr.join('.');
	}

	return '-';
}

export function percentage(
	input: string | number | undefined | null | big,
	hasSuffix = false,
	decimals = 1,
	noVal = '-',
	removeTrailingZeros = false
): string {
	if (input !== null && input !== undefined && input !== '' && !isNaN(Number(input))) {
		const suffix = hasSuffix ? '%' : '';
		let percentageValue = big(input).times(100).toFixed(decimals);
		if (removeTrailingZeros) {
			percentageValue = parseFloat(percentageValue).toString();
		}
		return percentageValue + suffix;
	}

	return noVal;
}

export function currency(input: string | number | big | null, decimals = 2, symbol = '$'): string {
	if (input !== null && input !== '' && !isNaN(Number(input))) {
		const inputWithCommas = new big(input).toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

		if (inputWithCommas.charAt(0) === '-') {
			return '-' + symbol + inputWithCommas.slice(1);
		} else {
			return symbol + inputWithCommas;
		}
	}
	return '';
}

export function currencyMillions(
	input: string | number | null,
	condensed = false,
	symbol = '$',
	separator = false
): string {
	if (input !== null && input !== '' && !isNaN(Number(input))) {
		const bigInput = new big(input);
		const suffix = condensed ? 'M' : 'million';
		const oneMillion = new big(1000000);
		const spacer = separator ? ' ' : '';
		return `${symbol}${bigInput.div(oneMillion).toFixed(1)}${spacer}${suffix}`;
	}

	return '';
}

export function bytes(bytes: number, precision = 1): string {
	try {
		big(bytes);
		const units = ['bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];
		const number = Math.floor(Math.log(bytes) / Math.log(1024));
		const reducedNumber = (bytes / Math.pow(1024, Math.floor(number))).toFixed(precision);

		return `${reducedNumber} ${units[number]}`;
	} catch {
		return '-';
	}
}

export function telephone(input: string): string {
	if (!input) {
		return '';
	}

	const value = input.toString().trim().replace(/^\+/, '');

	if (value.match(/[^0-9]/)) {
		return input;
	}

	let country, city, number;

	switch (value.length) {
		case 10:
			country = '';
			city = value.slice(0, 3);
			number = value.slice(3);
			break;
		case 11:
			country = value[0];
			city = value.slice(1, 4);
			number = value.slice(4);
			break;
		case 12:
			country = value.slice(0, 3);
			city = value.slice(3, 5);
			number = value.slice(5);
			break;
		default:
			return input;
	}

	if (country === '1') {
		country = '';
	}

	number = `${number.slice(0, 3)}-${number.slice(3)}`;

	return `${country} (${city}) ${number}`.trim();
}

export function enumToLabel(enumToConvert: string): string {
	const allLowersWithSpaces = enumToConvert.toLowerCase().split('_').join(' ');
	return allLowersWithSpaces.charAt(0).toUpperCase() + allLowersWithSpaces.slice(1);
}

export function labelToKebab(labelToConvert: string): string {
	if (!labelToConvert) return '';

	return labelToConvert.toLowerCase().split(' ').join('-');
}

export function enumToKebab(enumToConvert: string): string {
	return enumToConvert.toLowerCase().split('_').join('-');
}

export function camelLettersToKebab(camelToConvert: string): string {
	if (!camelToConvert) return '';

	return camelToConvert
		.split(/(?=[A-Z])/)
		.join('-')
		.toLowerCase();
}

export function stringToShortenedCurrency(toShorten: string): string {
	if (!toShorten) {
		return '';
	}

	const oneBillion = big(1.0e9);
	const oneMillion = big(1.0e6);
	const oneThousand = big(1.0e3);
	const toShortenBig = big(toShorten);

	const sign = toShortenBig.s === -1 ? '-' : '';

	const absNumberValue = toShortenBig.abs();
	let divisor = big(1);
	let suffix = '';

	if (absNumberValue.gte(oneBillion)) {
		divisor = oneBillion;
		suffix = 'B';
	} else if (absNumberValue.gte(oneMillion)) {
		divisor = oneMillion;
		suffix = 'M';
	} else if (absNumberValue.gte(oneThousand)) {
		divisor = oneThousand;
		suffix = 'k';
	}

	let formatted = `${absNumberValue.div(divisor).round(2, 0)}`;

	if (formatted.replace('.', '').length < 3) {
		formatted = big(formatted).toFixed(2);
	}

	return `${sign}$${formatted.substring(0, 4) + suffix}`;
}

export function realEstateStrategyColor(strategyId: string): string {
	const colorMap = realEstateStrategyColors;
	const filtered = colorMap.filter((strategy) => strategy.id === strategyId);
	return filtered.length > 0 ? filtered[0].color : '';
}

export function date(dateString: string, format = 'll'): string {
	if (!dateString) {
		return '';
	}

	return moment.parseZone(dateString).format(format);
}

export function capitalize(value: string | null): string {
	if (!value) return '';
	const lowerCaseWord = value.toLowerCase();
	return lowerCaseWord.charAt(0).toUpperCase() + lowerCaseWord.slice(1);
}

export function cityStateZip(address: Address): string {
	return `${address.city}, ${address.state} ${address.zip}`;
}
